import { service } from '@ember/service';
import { isPresent } from '@ember/utils';
import type StoreService from '@ember-data/store';
import { task, dropTask, restartableTask } from 'ember-concurrency';
import type BillingCompanyModel from 'garaje/models/billing-company';
import type CompanyModel from 'garaje/models/company';
import type FlowModel from 'garaje/models/flow';
import type LocationModel from 'garaje/models/location';
import type TenantModel from 'garaje/models/tenant';
import type TenantConnectionRequestModel from 'garaje/models/tenant-connection-request';
import type UserModel from 'garaje/models/user';
import type FeatureFlagsService from 'garaje/services/feature-flags';
import { FlowType, TenantConnectionStatus } from 'garaje/utils/enums';
import type { RecordArray } from 'garaje/utils/type-utils';
import type { Constructor } from 'type-fest';

export interface CompanyMeta {
  'employees-count': number;
  'active-locations-count': number;
}

// Flow#type comes back from the real API as `null` for Visitor flows but the FlowType enum uses '' for VISITOR_SCREENING
function flowType(flow: FlowModel): FlowType {
  return flow.type ?? FlowType.VISITOR_SCREENING;
}

// eslint-disable-next-line @typescript-eslint/explicit-module-boundary-types, @typescript-eslint/no-explicit-any
function withUserStateFunctionality<TBase extends Constructor<any>>(Base: TBase) {
  class withUserStateFunctionality extends Base {
    declare currentUser: UserModel | null;
    declare _companyId?: string | null;

    declare currentCompany: CompanyModel | null;
    declare billingCompany: BillingCompanyModel | null;
    declare currentLocation: LocationModel | null;

    declare tenantConnections: RecordArray<TenantModel> | null;
    declare tenantConnectionRequests: RecordArray<TenantConnectionRequestModel> | null;

    declare _companyMeta: CompanyMeta | null;

    @service declare featureFlags: FeatureFlagsService;
    @service declare store: StoreService;

    initUserState = dropTask(async () => {
      this.currentUser = await this._fetchCurrentUser.perform();
    });

    _setupUserAndCompanyFeatureFlags(currentUser: UserModel, currentCompany: CompanyModel) {
      const properties = {
        ['user']: currentUser,
        ['company']: currentCompany,
      };
      // eslint-disable-next-line ember/use-ember-get-and-set
      this.featureFlags.setProperties(properties);
      return this.featureFlags.ready();
    }

    _fetchCurrentUser = restartableTask(async () => {
      return await this.store.queryRecord('user', { id: 'me' });
    });

    loadTenantConnectionRequests = dropTask(async () => {
      this.tenantConnectionRequests = await this.store.query('tenant-connection-request', {
        id: 'my-connection-requests',
        include: 'tenant,connection-request-invites',
      });
    });

    loadTenantConnections = dropTask(async () => {
      this.tenantConnections = await this.store.query('tenant', {
        filter: {
          status: TenantConnectionStatus.CONNECTED,
        },
      });
    });

    // **************************************************************************
    // Visitors specific data
    // **************************************************************************

    // Track last task parameters for cache validation
    _lastFlowsTaskParams?: {
      locationId: string;
      includePropertyFlows: boolean;
      excludeEmployeeFlows: boolean;
    };

    loadFlows({
      includePropertyFlows = false,
      locationId = null,
      reload = true,
      excludeEmployeeFlows = false,
    }: {
      includePropertyFlows?: boolean;
      locationId?: string | null;
      reload?: boolean;
      excludeEmployeeFlows?: boolean;
    } = {}) {
      const lastResponse = this._loadFlowsTask.last;
      const location = isPresent(locationId) ? locationId : this.currentLocation!.id;

      // Check if we can reuse the last task by comparing parameters
      let canUseLastTask = false;
      if (lastResponse && !reload && this._lastFlowsTaskParams) {
        const isSameLocation = this._lastFlowsTaskParams.locationId === location;
        const samePropertyFlows = this._lastFlowsTaskParams.includePropertyFlows === includePropertyFlows;
        const sameExcludeEmployeeFlows = this._lastFlowsTaskParams.excludeEmployeeFlows === excludeEmployeeFlows;
        canUseLastTask = isSameLocation && samePropertyFlows && sameExcludeEmployeeFlows;
      }

      if (canUseLastTask && lastResponse && lastResponse.isRunning) {
        // return the already running query task if it's currently running
        return lastResponse;
      } else if (canUseLastTask && !reload && lastResponse && lastResponse.value) {
        // if the flow task is not running and we're not reloading
        // pull in flows that might be in the store
        let flows = (lastResponse.value ?? []).filter((flow) => !flow.isDestroyed);
        const flowIds = flows?.map((flow) => flow.id);
        const flowsInStore = this.store.peekAll('flow').filter((flow) => flow.belongsTo('location').id() === location);

        flowsInStore.forEach((flow) => {
          // add flows that might've been added in previous ui flows
          if (!flowIds?.includes(flow.id)) {
            flows?.push(flow);
          }
        });

        if (excludeEmployeeFlows) {
          flows = flows.filter((flow) => flowType(flow) !== FlowType.EMPLOYEE_SCREENING && !flow.employeeCentric);
        }

        // return a promise so this function always returns a promise
        // making the function async would mean resolving the task in the other block, might cause issues
        return new Promise<FlowModel[]>((resolve) => {
          return resolve(flows);
        });
      } else {
        // Store parameters for next cache check
        this._lastFlowsTaskParams = {
          locationId: location,
          includePropertyFlows,
          excludeEmployeeFlows,
        };
        return this._loadFlowsTask.perform(location, includePropertyFlows, excludeEmployeeFlows);
      }
    }

    _loadFlowsTask = task(async (locationId: string, includePropertyFlows: boolean, excludeEmployeeFlows?: boolean) => {
      const includes = ['sign-in-field-actions', 'actionable-sign-in-field-actions', 'actionable-sign-in-fields'];
      const filter: { location: string; property_owned?: string; type: string } = {
        location: locationId,
        type: 'all',
      };
      if (includePropertyFlows) {
        filter['property_owned'] = 'true,false';
      }

      const include = [
        'agreement-page',
        'active-user-document-template-configurations',
        'active-user-document-template-configurations.user-document-template',
        'flow-visit-workflow',
        'flow-questionnaires',
        'flow-questionnaires.flow',
        'flow-questionnaires.questionnaire',
      ];
      include.push(...includes.map((include) => `sign-in-field-page.sign-in-fields.${include}`));

      let flows = (await this.store.query('flow', { include: include.join(','), filter })).slice();

      // Apply client-side filters
      if (excludeEmployeeFlows) {
        flows = flows.filter((flow) => flowType(flow) !== FlowType.EMPLOYEE_SCREENING && !flow.employeeCentric);
      }

      return flows;
    });
  }

  return withUserStateFunctionality;
}

export default withUserStateFunctionality;
